import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Product } from 'types'
import { KEY, resetBootstrapETag } from './useAllItemsBootstrap'

type AllItems = { organizations: any[]; deals: any[]; products: any[]; parentClients: any[] }

export interface SyncProductsArgs {
  tableName: string
  PK: string
  deal_uuid: string
  products: Product[]
  deletedProducts: string[]
}

const syncProducts = async ({ PK, deal_uuid, products, deletedProducts }: SyncProductsArgs) => {
  console.log('[syncProducts] called with:', { PK, deal_uuid, products })
  const url = `${baseUrl}/aws/organizations_table/sync-products`
  try {
    const response = await axios.post(url, { PK, deal_uuid, products, deletedProducts })
    if (response.status !== 200) throw new Error('Network response was not ok')
    return response.data
  } catch (error) {
    console.error('Failed to sync products:', error)
    throw error
  }
}

// Apply product delta to cached bootstrap data (mirrors backend patchBootstrapCache)
function applyProductDelta(prev: AllItems, upserts: any[], deletes: string[]): AllItems {
  if (!prev) return prev

  const deleteSet = new Set(deletes)
  // Remove deleted products
  let products = (prev.products ?? []).filter(p => !deleteSet.has(p.SK))

  // Upsert: update existing or add new
  const bySK = new Map(products.map(p => [p.SK, p]))
  for (const u of upserts) {
    bySK.set(u.SK, u)
  }
  products = Array.from(bySK.values())

  return { ...prev, products }
}

export const useSyncProducts = () => {
  const qc = useQueryClient()
  const { showSnackbar } = useSnackbarContext()

  return useMutation({
    mutationFn: syncProducts,
    onSuccess: async (data, variables) => {
      console.log('[useSyncProducts] Synced products:', data, variables)
      showSnackbar(
        `${data?.message} | added: ${data?.added} | updated: ${data?.updated} | deleted: ${data?.deleted}`,
        'success'
      )

      // Cancel in-flight queries to prevent stale overwrites
      await qc.cancelQueries({ queryKey: KEY })

      // 👇 tell health hook to chill for ~4s
      ;(globalThis as any).__BOOTSTRAP_SUPPRESS_UNTIL__ = Date.now() + 4000

      // Patch cache directly using response data (no full refetch!)
      // Backend returns: { upserts: Product[], deletes: string[] }
      const { upserts = [], deletes = [] } = data
      if (upserts.length > 0 || deletes.length > 0) {
        qc.setQueryData<AllItems>(KEY, prev => {
          if (!prev) return prev
          return applyProductDelta(prev, upserts, deletes)
        })
        // Reset ETag so next background refresh gets fresh data
        resetBootstrapETag(qc)
      }

      // Notify observers to re-render with patched data (no network request)
      qc.invalidateQueries({ queryKey: KEY, refetchType: 'none' })
    },
    onError: error => {
      console.error('[useSyncProducts] Error syncing products:', error)
    },
  })
}

// export const useSyncProducts = () => {
//   const queryClient = useQueryClient()
//   const { showSnackbar } = useSnackbarContext()

//   return useMutation({
//     mutationFn: syncProducts,
//     onSuccess: async (data, variables) => {
//       console.log('[useSyncProducts] Synced products:', data, variables)
//       // Optionally invalidate org or deal cache here
//       // queryClient.invalidateQueries({ queryKey: ['deal', variables.deal_uuid] })
//       showSnackbar(
//         `${data?.message} | added: ${data?.added} | updated: ${data?.updated} | deleted: ${data?.deleted}`,
//         'success'
//       )
//       await queryClient.invalidateQueries({
//         queryKey: ['organizations_table', 'allItems'],
//         refetchType: 'active',
//       })
//     },
//     onError: error => {
//       console.error('[useSyncProducts] Error syncing products:', error)
//     },
//   })
// }
